import { Injectable, computed, effect, inject, signal } from '@angular/core';
import type { Unsubscribe } from 'firebase/firestore';

import { ProductAccessConfig } from '../models/product.model';
import { OrgContextService } from './org-context.service';
import { ProductService } from './product.service';

/** Definitive access state; `unknown` only ever means "still loading". */
export type ProductAccessState = 'unknown' | 'granted' | 'denied';

const ENTITLEMENT_TIMEOUT_MS = 5_000;
/** How long a server-confirmed grant stays good for subsequent navigations. */
const GRANT_FRESHNESS_MS = 15_000;

/**
 * Pure access evaluator — identical to `build`'s and `app`'s, which both
 * document themselves as mirroring admin-console/dev-console (A1).
 *
 *   1. No config → DENIED.
 *   2. Org level: enabledProducts must include the product.
 *   3. Role level: a non-empty roleProductAccess[roleId] is an allowlist.
 *      Missing entry or empty array = no restriction for that role.
 *
 * Rule 3 is the fix: the previous `!Array.isArray(g) || g.includes(id)` denied
 * on an empty array, because `!Array.isArray([])` is `false` and `[].includes()`
 * is `false`. The suite tolerates `[]` precisely because the admin UI produces it.
 */
export function evaluateAccess(
  config: ProductAccessConfig | null,
  roleId: string | null,
  productId: string,
): boolean {
  if (!config) return false;
  if (!config.enabledProducts.includes(productId)) return false;

  if (roleId) {
    const roleProducts = config.roleProductAccess[roleId];
    if (
      Array.isArray(roleProducts) &&
      roleProducts.length > 0 &&
      !roleProducts.includes(productId)
    ) {
      return false;
    }
  }

  return true;
}

/**
 * Live product entitlement for the active organization (A7).
 *
 * Wraps `ProductService`'s config document in a signal so route guards and the
 * workspace can both ask "does this org still have Create?" and react the moment
 * the answer changes — including when the user switches organization while a
 * workspace is open.
 *
 * Entitlement rules mirror the rest of the platform:
 *  - the product must appear in `enabledProducts`;
 *  - if the member's role has an explicit `roleProductAccess` list, the product
 *    must appear there too. No entry means "not restricted by role".
 */
@Injectable({ providedIn: 'root' })
export class ProductAccessService {
  private readonly productService = inject(ProductService);
  private readonly orgContext = inject(OrgContextService);

  /**
   * Three states, and the distinction matters (A1): `undefined` is "not loaded
   * yet", `null` is "the org has no product-access document" — which is now a
   * definitive denial rather than a legacy allowance. Collapsing them would
   * leave a config-less org stuck on `'unknown'` forever.
   */
  private readonly config = signal<ProductAccessConfig | null | undefined>(undefined);
  private readonly subscribedOrgId = signal<string | null>(null);
  private subscribedRoleId: string | null = null;
  private unsubscribe: Unsubscribe | null = null;

  /**
   * Expiry per `${orgId}:${productId}` for a server-confirmed grant.
   *
   * The layout guard runs on every child navigation, so without this each
   * in-app link cost an uncached round-trip and a network blip mid-session
   * bounced the user to `/product-disabled`. Only *grants* are held, and only for a
   * few seconds: a denial always re-checks, and an org switch, a role change
   * or a live config push drops the entry immediately, so a revocation is
   * still honoured on the next navigation rather than after the window.
   */
  private readonly confirmedGrants = new Map<string, number>();

  constructor() {
    // Re-point the subscription whenever the active org (or role) changes so a
    // switch immediately re-evaluates entitlement instead of keeping the old
    // org's answer.
    effect(() => {
      const orgId = this.orgContext.currentOrg()?.id ?? null;
      const roleId =
        this.orgContext.currentRole()?.id ?? this.orgContext.currentMember()?.roleId ?? null;
      if (roleId !== this.subscribedRoleId) {
        // A role change alone leaves the subscription in place, but it changes
        // what isAllowed answers, so any confirmed grant is stale.
        this.subscribedRoleId = roleId;
        this.confirmedGrants.clear();
      }
      if (orgId === this.subscribedOrgId()) return;
      const previousOrgId = this.subscribedOrgId();
      this.unsubscribe?.();
      this.unsubscribe = null;
      if (previousOrgId) this.productService.clearProductAccessCache(previousOrgId);
      this.confirmedGrants.clear();
      this.config.set(undefined);
      this.subscribedOrgId.set(orgId);
      if (!orgId) return;
      this.unsubscribe = this.productService.subscribeToConfig(orgId, (config) => {
        // Any server-pushed config invalidates confirmed grants for this org,
        // so a revocation cannot be masked by the freshness window.
        this.forgetConfirmedGrants(orgId);
        this.config.set(config);
        if (!this.isAllowed(config, 'create')) this.productService.clearProductAccessCache(orgId);
      });
    });
  }

  /** Live entitlement state for `productId` in the active organization. */
  state(productId: string) {
    return computed<ProductAccessState>(() => {
      const orgId = this.orgContext.currentOrg()?.id ?? null;
      const config = this.config();
      if (!orgId) return 'denied';
      if (config === undefined || this.subscribedOrgId() !== orgId) return 'unknown';
      return this.isAllowed(config, productId) ? 'granted' : 'denied';
    });
  }

  /**
   * One-shot check used by the route guard. Every new answer comes from a
   * fresh server response; the only value ever reused is a *grant* that the
   * server confirmed within the last `GRANT_FRESHNESS_MS`, and an org switch,
   * role change or live config push drops it before the window ends. Denials
   * are never reused; offline, timeout and read errors all fail closed.
   */
  async hasProduct(orgId: string, productId: string): Promise<boolean> {
    const grantKey = `${orgId}:${productId}`;
    if ((this.confirmedGrants.get(grantKey) ?? 0) > Date.now()) return true;

    // A timed-out or failed read is not a server denial. It is retried once
    // (keeping the per-attempt budget) so a slow cold load does not present a
    // fully entitled org with "Product Not Available".
    for (let attempt = 0; attempt < 2; attempt++) {
      let timer: ReturnType<typeof setTimeout> | undefined;
      try {
        const config = await Promise.race([
          this.productService.getProductAccessConfigFromServer(orgId),
          new Promise<never>((_, reject) => {
            timer = setTimeout(
              () => reject(new Error('Entitlement check timed out.')),
              ENTITLEMENT_TIMEOUT_MS,
            );
          }),
        ]);
        const allowed = this.isAllowed(config, productId);
        if (allowed) this.confirmedGrants.set(grantKey, Date.now() + GRANT_FRESHNESS_MS);
        else {
          this.confirmedGrants.delete(grantKey);
          this.productService.clearProductAccessCache(orgId);
        }
        return allowed;
      } catch {
        // Retry once; the second failure falls through to the closed default.
      } finally {
        if (timer) clearTimeout(timer);
      }
    }
    // Still no definitive answer: the guard must not fail open.
    this.confirmedGrants.delete(grantKey);
    this.productService.clearProductAccessCache(orgId);
    return false;
  }

  private forgetConfirmedGrants(orgId: string): void {
    for (const key of [...this.confirmedGrants.keys()]) {
      if (key.startsWith(`${orgId}:`)) this.confirmedGrants.delete(key);
    }
  }

  private isAllowed(config: ProductAccessConfig | null, productId: string): boolean {
    const roleId =
      this.orgContext.currentRole()?.id ?? this.orgContext.currentMember()?.roleId ?? null;
    return evaluateAccess(config, roleId, productId);
  }
}
